import React, { useEffect, useRef, useState } from "react";

// Fade + subtle upward-slide reveal, triggered once by IntersectionObserver
// when the element first enters the viewport. Respects
// `prefers-reduced-motion` — reduced-motion visitors get the final state
// immediately with no transition.
//
// Layout note: the initial state uses opacity + transform only, so the
// element still occupies its final size/position. No CLS caused by reveal.
export const Reveal = ({
  as: Tag = "div",
  delay = 0,
  variant = "default", // "default" | "dramatic"
  className = "",
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Reduced-motion or missing IO support → skip observation, appear now.
    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    // Trigger geometry — reveals fire as the content is actually entering
    // the viewport (not before), so users see the movement rather than
    // arriving to already-completed animations. Default variant uses a
    // 10% intersection threshold with no pre-fold rootMargin. The
    // dramatic variant (Services cards) uses the same shape with a
    // slightly-higher 12% threshold for its more pronounced 28px slide.
    const observerOpts =
      variant === "dramatic"
        ? { rootMargin: "0px", threshold: 0.12 }
        : { rootMargin: "0px", threshold: 0.10 };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target); // trigger-once
          }
        }
      },
      observerOpts
    );
    io.observe(el);
    return () => io.disconnect();
  }, [variant]);

  return (
    <Tag
      ref={ref}
      className={`ur-reveal${variant === "dramatic" ? " ur-reveal--dramatic" : ""}${inView ? " ur-reveal-in" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};
